import React, { useState, useEffect, useRef } from "react";
import MenuHeader from "./MenuHeader";
import Modal from 'react-modal';
import useApi from "../hooks/useApi";
import { castDate, asTitle } from '../shared/utils/string';
import PromotionService from "../services/promotion.service";
import UserService from "../services/user.service";
import ZoneService from "../services/zone.service";
import Select from 'react-select';
import ProductService from "../services/product.service";
import MultilingualInput from "./MultilingualInput";
import { Menu, MenuList, MenuItem, IconButton, ListItemText, ListItemIcon } from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from "react-i18next";
import { useTranslatableData } from '../hooks/useTranslatable';
import AsyncSelect from 'react-select/async';
import StatusBadge from "./TaskList/StatusBadge.js";


const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    scrollbarWidth: 'none',
    height: '680px',
  },
};

const customSelectStyles = {
  control: (styles) => ({
    ...styles,
    width: '100%',
    height: '60px',
    padding: '0 15px',
    fontSize: '14px',
    lineHeight: '60px',
    color: '#393D44',
    border: '0',
    borderRadius: '20px',
    background: '#ffffff',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none',
  }),
};

const BUSINESS_TIME_ZONE = 'Europe/Zurich';

const formatPromotionDate = (value, locale = 'fr-FR') => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(date);
};

const formatPromotionDateTime = (value, locale = 'fr-FR') => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(date);
};

const SUCCESS_TRANSACTION_STATES = ['AUTHORIZED', 'COMPLETED', 'FULFILL'];
const PENDING_TRANSACTION_STATES = ['PENDING', 'CONFIRMED', 'PROCESSING'];
const FAILED_TRANSACTION_STATES = ['FAILED', 'DECLINE', 'VOIDED'];
const PROMOTION_LIST_RELATIONS = 'clients,product,zones,address,transaction,usage,usage.client,usage.booking.products.category,usage.booking.therapists,usage.booking.address,usage.booking.transactions';

const ensurePromotionRelations = (withValue = '') => {
  const relations = new Set(withValue.split(',').filter(Boolean));

  PROMOTION_LIST_RELATIONS.split(',').forEach((relation) => relations.add(relation));

  return Array.from(relations).join(',');
};

const normalizeLimit = (value) => {
  if (value === null || value === undefined || value === '') {
    return -1;
  }

  return Number(value);
};

const formatLimit = (value) => {
  const limit = normalizeLimit(value);
  return limit === -1 ? '∞' : limit;
};

const getPrimaryTransaction = (promo) => {
  if (!Array.isArray(promo?.transaction) || promo.transaction.length === 0) {
    return null;
  }

  return promo.transaction[0];
};

const getTransactionTone = (status) => {
  if (SUCCESS_TRANSACTION_STATES.includes(status)) {
    return 'success';
  }

  if (PENDING_TRANSACTION_STATES.includes(status)) {
    return 'warning';
  }

  if (FAILED_TRANSACTION_STATES.includes(status)) {
    return 'danger';
  }

  return 'neutral';
};

const getUsedUsageCount = (promo) => (
  Array.isArray(promo?.usage)
    ? promo.usage.filter((usage) => usage?.is_used).length
    : 0
);

const getUsageSummary = (promo) => {
  const used = getUsedUsageCount(promo);
  const totalLimit = normalizeLimit(promo?.total_usage);
  const clientLimit = normalizeLimit(promo?.usage_per_customer);
  const remaining = totalLimit === -1 ? -1 : Math.max(totalLimit - used, 0);

  return {
    used,
    totalLimit,
    clientLimit,
    remaining,
    isUnlimited: totalLimit === -1,
  };
};

const getDisplayName = (value) => {
  if (!value) {
    return '';
  }

  return typeof value === 'string' ? value : (value.fr || value.en || '');
};

const getUsageClientLabel = (usage) => (
  usage?.client?.extra?.full_name
  || usage?.client?.name
  || usage?.client?.email
  || (usage?.client_id ? `#${usage.client_id}` : '-')
);

const formatMoney = (value, currency = 'CHF') => {
  const amount = Number(value);

  if (Number.isNaN(amount)) {
    return '-';
  }

  return `${amount.toFixed(Number.isInteger(amount) ? 0 : 2)} ${currency}`;
};

const getProductLabel = (product) => {
  const title = getDisplayName(product?.name)
    || getDisplayName(product?.category?.title)
    || product?.category?.title
    || (product?.id ? `#${product.id}` : '-');
  const duration = product?.duration ? `${product.duration} min` : '';

  return [title, duration].filter(Boolean).join(' - ');
};

const getBookingAddressLabel = (booking) => {
  const address = booking?.address;

  if (!address) {
    return '-';
  }

  return [
    address.street,
    address.street_number,
    address.zip_code,
    address.state || address.city,
    address.country_code,
  ].filter(Boolean).join(' ');
};

const getBookingProductTotal = (booking) => (
  Array.isArray(booking?.products)
    ? booking.products.reduce((sum, product) => sum + Number(product?.price || 0), 0)
    : 0
);

const getBookingUsageGroups = (usageRows) => {
  const groups = new Map();

  usageRows.forEach((usage) => {
    const booking = usage?.booking || null;
    const key = booking?.id ? `booking-${booking.id}` : `unlinked-${usage.id}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        booking,
        usageRows: [],
        latestUsageAt: usage?.created_at,
      });
    }

    const group = groups.get(key);
    group.usageRows.push(usage);

    if (usage?.created_at && (!group.latestUsageAt || new Date(usage.created_at) > new Date(group.latestUsageAt))) {
      group.latestUsageAt = usage.created_at;
    }
  });

  return Array.from(groups.values())
    .sort((a, b) => new Date(b.latestUsageAt || 0) - new Date(a.latestUsageAt || 0));
};

const getBookingPaymentLabel = (booking) => {
  if (!booking) {
    return '-';
  }

  if (booking.offline_payment) {
    return 'Package / offline';
  }

  const transaction = Array.isArray(booking.transactions) ? booking.transactions[0] : null;
  return transaction?.status || '-';
};

const getBusinessStatus = (promo) => {
  const transaction = getPrimaryTransaction(promo);
  const transactionState = transaction?.status;
  const usage = getUsageSummary(promo);

  if (promo?.date_validity && !String(promo.date_validity).startsWith('9999')) {
    const validityDate = new Date(promo.date_validity);
    if (!Number.isNaN(validityDate.getTime()) && validityDate < new Date()) {
      return { code: 'expired', tone: 'danger' };
    }
  }

  if (!usage.isUnlimited && usage.remaining === 0) {
    return { code: 'exhausted', tone: 'warning' };
  }

  if (promo?.is_gift) {
    if (!transaction) {
      return promo?.is_active
        ? { code: 'activeNoTransaction', tone: 'warning' }
        : { code: 'noTransaction', tone: 'danger' };
    }

    if (SUCCESS_TRANSACTION_STATES.includes(transactionState) && !promo?.is_active) {
      return { code: 'paidInactive', tone: 'danger' };
    }

    if (PENDING_TRANSACTION_STATES.includes(transactionState) && !promo?.is_active) {
      return { code: 'paymentPending', tone: 'warning' };
    }

    if (FAILED_TRANSACTION_STATES.includes(transactionState)) {
      return { code: 'paymentFailed', tone: 'danger' };
    }
  }

  return promo?.is_active
    ? { code: 'active', tone: 'success' }
    : { code: 'disabled', tone: 'muted' };
};

const statusPillStyle = (tone) => {
  const tones = {
    success: { background: '#E9F8EA', color: '#67BB6A' },
    warning: { background: '#FFF4D9', color: '#C27A00' },
    danger: { background: '#FFE5E1', color: '#D9432F' },
    muted: { background: '#F1F1F1', color: '#8B8B8B' },
    neutral: { background: '#EEF3F8', color: '#506070' },
  };

  return {
    ...(tones[tone] || tones.neutral),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 24,
    padding: '3px 8px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
};

const Promotion = () => {
  const { t } = useTranslation('promotion');
  const [modalIsOpen, setIsOpen] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [clients, setClients] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null); // For editing
  const [detailsModalIsOpen, setDetailsModalIsOpen] = useState(false);
  const api = useApi();
  const [alertMessage, setAlertMessage] = useState('');
  const [formError, setFormError] = useState('');

  const [promotionFormData, setPromotionFormData] = useState({
    id: '',
    name: { en: '', fr: '' },
    code: '',
    date_validity: '',
    date_creation: new Date().toISOString(),
    isUnlimited: false,
    type: 'PERCENT_OFF',
    amount: null,
    assigned_to: 'One client',
    total_usage: null,
    usage_per_customer: null,
    notifyClients: false,
    notifyEmails: false,
    clients: [],
    zones: [],
    product_id: null,
    is_active: 1,
    all_clients: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState({
    with: PROMOTION_LIST_RELATIONS,
    page: currentPage,
    per_page: perPage
  });

  const [sortDirection, setSortDirection] = useState(true);
  const [filterData, setFilterData] = useState({
    search: "",
  });

  const timeoutRef = useRef(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [productList, setProductList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const translatedProductList = useTranslatableData(productList, ['name', 'domain.title', 'category.title', 'category.parent.title']);
  const translatedPromotions = useTranslatableData(promotions, ['name']);
  
  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const loadClientsOptions = async (inputValue, callback) => {
    try {
        if(!inputValue || inputValue.length < 3) {
            callback([]);
            return;
        }
        const response = await UserService.indexByRole(
            { with: 'primary_address', status: '!eq:BLOCKED', name: `contains:${inputValue}` },
            'ROLE_CLIENT',
            api
        );
        const mergedClients = [...clients];
    
        response.forEach(newClient => {
          if (!mergedClients.find(client => client.id === newClient.id)) {
            mergedClients.push(newClient);
          }
        });
        
        setClients(mergedClients);
        const options = response.map(customer => ({
            value: customer.id,
            label: `${customer.id} - ${customer.extra?.full_name}`
        }));
        callback(options);
    } catch (error) {
        console.error("Error loading customer options", error);
        callback([]);
    }
};

  useEffect(() => {
    fetchPromotions();
  }, [currentPage, perPage]);
  useEffect(() => {
    fetchClients();
  }, []);
  useEffect(() => {
    const preventScroll = (e) => {
      if (e.target.type === 'number') {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', preventScroll, { passive: false });
    return () => {
      window.removeEventListener('wheel', preventScroll);
    };
  }, []);
  const fetchPromotions = async (updatedFilters = filters) => {
    try {
      const requestFilters = {
        ...updatedFilters,
        sort: '-created_at',
        page: updatedFilters.page || currentPage,
        per_page: updatedFilters.per_page || perPage,
        with: ensurePromotionRelations(updatedFilters.with),
      };
      const response = await PromotionService.index(requestFilters, api);
      // const response = await PromotionService.index(updatedFilters, api);
      // const sortedPromotions = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      // setPromotions(sortedPromotions);
      setPromotions(response.data);
      setTotal(response.total);
      setLastPage(response.last_page);
      if (response.last_page < currentPage) {
        setFilters({ ...updatedFilters, page: response.last_page })
        setCurrentPage(response.last_page);
      }
    } catch (error) {
      console.error("Error fetching promotions", error);
    }
  };
  const fetchPromotionsSort = async (updatedFilters = filters) => {
    try {
      const response = await PromotionService.index({
        ...updatedFilters,
        with: ensurePromotionRelations(updatedFilters.with),
      }, api);
      setPromotions(response.data);
      setTotal(response.total);
      setLastPage(response.last_page);
      if (response.last_page < currentPage) {
        setFilters({ ...updatedFilters, page: response.last_page })
        setCurrentPage(response.last_page);
      }
    } catch (error) {
      console.error("Error fetching promotions", error);
    }
  };
  const fetchClients = async (promotion = null) => {
    try {
      if (promotion) {
        const clientsPromoIds = promotion.clients.map((client) => client.id)
        if(clientsPromoIds.length > 0) {
          const response = await UserService.indexByRole({ id: `in:${clientsPromoIds.join(',')}`, with: 'primary_address', status: '!eq:BLOCKED' }, 'ROLE_CLIENT', api);
          setClients(response);
        } else {
          setClients([]);
        }
      }
      const responsez = await ZoneService.index({}, api);
      // console.log('ZoneService', responsez)
      setZones(responsez);
    } catch (error) {
      console.error("Error fetching clients", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'assigned_to') {
      setFormError('');
      setPromotionFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        all_clients: value === "All",
        clients: value === "All" ? [] : prev.clients,
        zones: prev.zones,
      }));
    } else {
      setPromotionFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleMultilingualChange = (fieldName, value) => {
    setPromotionFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleClientsChange = (selected) => {
    setFormError('');
    if (promotionFormData.assigned_to === "One client") {
      setPromotionFormData((prev) => ({
        ...prev,
        clients: selected ? [selected.value] : [],
      }));
    } else if (promotionFormData.assigned_to === "Group of clients") {
      setPromotionFormData((prev) => ({
        ...prev,
        clients: selected ? selected.map((client) => client.value) : [],
      }));
    }
  };

  const handleZonesChange = (selected) => {
    setPromotionFormData((prev) => ({
      ...prev,
      zones: selected ? selected.map((zone) => zone.value) : [],
    }));
  };

  const openModal = async (promotion = null) => {
    setFormError('');
    if (promotion) {
      setSelectedPromotion(promotion);
      setPromotionFormData({
        ...promotion,
        name: typeof promotion.name === 'string' ? { en: promotion.name, fr: '' } : promotion.name || { en: '', fr: '' },
        clients: promotion.clients.map((client) => client.id),
        zones: promotion.zones.map((zone) => zone.id),
        assigned_to: promotion.all_clients
          ? "All"
          : promotion.clients.length > 1
            ? "Group of clients"
            : "One client",
        all_clients: promotion.all_clients || false,
      });
    } else {
      setSelectedPromotion(null);
      setPromotionFormData({
        name: { en: '', fr: '' },
        code: generatePromoCode(),
        date_validity: '',
        date_creation: new Date().toISOString(),
        isUnlimited: false,
        type: 'PERCENT_OFF',
        amount: '',
        assigned_to: 'One client',
        total_usage: '',
        usage_per_customer: '',
        notifyClients: false,
        notifyEmails: false,
        clients: [],
        zones: [],
        product_id: null,
        is_active: 1,
        all_clients: false,
      });
    }
    const products = await ProductService.index({ with: 'category,hotel' }, api);
    setProductList(products);
    await fetchClients(promotion);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setClients([]);
    setFormError('');
  }

  const generatePromoCode = (length = 10) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let promoCode = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      promoCode += characters.charAt(randomIndex);
    }
    return promoCode;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (
      !promotionFormData.is_gift &&
      promotionFormData.assigned_to !== "All" &&
      promotionFormData.clients.length === 0
    ) {
      setFormError(t('alert.clientRequired'));
      return;
    }

    try {
      const response = selectedPromotion
        ? await PromotionService.update(selectedPromotion.id, {
          ...promotionFormData,
          code: promotionFormData.code || selectedPromotion.code,
        }, api)
        : await PromotionService.store(promotionFormData, api);
      fetchPromotions();
      closeModal();
    } catch (error) {
      const validationMessage = error?.response?.data?.errors?.clients?.[0];
      setFormError(validationMessage || t('alert.saveFailed'));
    }
  };

  const renderAssignedClients = (promo) => {
    if (promo.is_gift) return null;
    if (promo.all_clients) return t('table.values.allRegisteredClients');
    if (!promo.clients?.length) return t('table.values.noClientsAssigned');

    return (
      <>
        <div className={`collapsible ${expandedRows[promo.id] ? "expanded" : ""}`}>
          {promo.clients.map((client) => client.extra?.full_name).join(", ")}
        </div>
        {promo.clients.length > 2 && (
          <span className="show-more" onClick={() => toggleRow(promo.id)}>
            {expandedRows[promo.id] ? t('table.values.showLess') : t('table.values.showMore')}
          </span>
        )}
      </>
    );
  };

  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.extra?.full_name,
  }));

  const zoneOptions = zones.map((zone) => ({
    value: zone.id,
    label: zone.name,
  }));

  const selectedClients = clientOptions.filter((option) => promotionFormData.clients.includes(option.value));
  const selectedZones = zoneOptions.filter((option) => promotionFormData.zones.includes(option.value));

  const handleNextPage = () => {
    setFilters({ ...filters, page: currentPage + 1 });
    setCurrentPage(currentPage + 1);
  }

  const handlePrevPage = () => {
    setFilters({ ...filters, page: currentPage - 1 });
    setCurrentPage(currentPage - 1);
  }

  const handleLastPage = () => {
    setFilters({ ...filters, page: lastPage });
    setCurrentPage(lastPage);
  }

  const handleFirstPage = () => {
    setFilters({ ...filters, page: 1 });
    setCurrentPage(1);
  }

  const handlePerPageChange = (e) => {
    setFilters({ ...filters, per_page: e.target.value, page: 1 });
    setPerPage(e.target.value);
    setCurrentPage(1);
  }

  const handleSelectedSort = async (event) => {
    const selected = document.querySelector('.sort-header.active');
    let direction = true;
    if (selected?.id !== event.target.id) {
      selected?.classList.remove('active');
      event.target.classList.add('active');
      setSortDirection(true);
    } else {
      setSortDirection(!sortDirection);
      direction = !sortDirection;
    }

    const sortField = event.target.id === 'name' ? `${direction ? '' : '-'}name` : `${direction ? '' : '-'}${event.target.id}`;
    setFilters({ ...filters, sort: sortField });
    filters.sort = sortField;

    await fetchPromotionsSort(); // Refresh data with new sort order
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();

    const searchValue = filterData.search.trim().toLowerCase();
    const filteredPromotion = promotions.filter((promotion) => {
      // Handle multilingual name field - check both en and fr values
      const nameToSearch = typeof promotion.name === 'string' 
        ? promotion.name 
        : (promotion.name?.en || '') + ' ' + (promotion.name?.fr || '');
      return nameToSearch.toLowerCase().includes(searchValue);
    });

    setPromotions(filteredPromotion);
  };

  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    setFilterData({
      ...filterData,
      [name]: value,
    });

    if (name === "search" && value === "") {
      await fetchPromotions();
    }

    if (name === "type") {
      const updatedFilters = { ...filters, type: value };
      await fetchPromotions(updatedFilters);
    }
  };

  const handleEditMouseEnter = (index) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredRow(index);
  };

  const handleEditMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredRow(null);
    }, 250);
  };

  const handleTypeChange = (e) => {
    const { value } = e.target;
    setPromotionFormData((prev) => ({
      ...prev,
      type: value,
      amount: value === "FREE" ? null : "", // Reset amount if FREE is selected
    }));
  };

  const productOptions = translatedProductList?.length > 0 ? translatedProductList.map(row => ({ value: row.id, label: `${row.hotel?.name || ' '} - ${row.category?.title || ' '} - ${row.duration}min`, price: row.price })) : []; const selectedProductOption = productOptions.find(o => o.value === promotionFormData.product_id) || null;
  const typeLabels = {
    PERCENT_OFF: "Percentage / % Off",
    FLATT_OFF: "Fixed Discount",
    // FREE_CREDITS: "Voucher / Credit to Use",
    FREE: "Free Service",
    DURATION: "Duration",
  };

  const handleClick = (event, index) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedIndex(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedIndex(null);
  };

  const blockPromotion = async (promo) => {
    await PromotionService.update(promo.id, { is_active: false }, api);
    fetchPromotions();
  };

  const unblockPromotion = async (promo) => {
    await PromotionService.update(promo.id, { is_active: true }, api);
    fetchPromotions();
  };

  const togglePromotionStatus = async (promo) => {
    if (isExpired(promo.date_validity)) {
      setAlertMessage(t('alert.expired'));
      setTimeout(() => {
        setAlertMessage('');
      }, 3000);
      return;
    }

    if (promo.is_active) {
      await blockPromotion(promo);
    } else {
      await unblockPromotion(promo);
    }
    handleClose();
  };
  const isExpired = (date) => {
    const currentDate = new Date();
    const validityDate = new Date(date);
    return validityDate < currentDate;
  };

  const getBusinessStatusLabel = (status) => (
    t(`businessStatus.${status.code}`, status.code)
  );

  const renderBusinessStatus = (promo) => {
    const status = getBusinessStatus(promo);

    return (
      <span style={statusPillStyle(status.tone)}>
        {getBusinessStatusLabel(status)}
      </span>
    );
  };

  const renderUsageCell = (promo) => {
    const usage = getUsageSummary(promo);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <strong>{usage.used} / {formatLimit(usage.totalLimit)}</strong>
      </div>
    );
  };

  const renderRemainingCell = (promo) => {
    const usage = getUsageSummary(promo);
    const value = usage.isUnlimited ? '∞' : usage.remaining;

    return (
      <span style={!usage.isUnlimited && usage.remaining === 0 ? statusPillStyle('warning') : undefined}>
        {value}
      </span>
    );
  };

  const renderPaymentCell = (promo) => {
    if (!promo.is_gift) {
      return '-';
    }

    const transaction = getPrimaryTransaction(promo);

    if (!transaction) {
      return (
        <span style={statusPillStyle('danger')}>
          {t('businessStatus.noTransaction')}
        </span>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <span style={statusPillStyle(getTransactionTone(transaction.status))}>
          {transaction.status || '-'}
        </span>
        <small style={{ color: '#777' }}>
          {[transaction.amount, transaction.currency].filter(Boolean).join(' ') || '-'}
        </small>
      </div>
    );
  };

  const selectedTransaction = selectedPromotion ? getPrimaryTransaction(selectedPromotion) : null;
  const selectedUsageSummary = selectedPromotion ? getUsageSummary(selectedPromotion) : null;
  const selectedUsageRows = selectedPromotion && Array.isArray(selectedPromotion.usage)
    ? [...selectedPromotion.usage].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    : [];
  const selectedUsageGroups = getBookingUsageGroups(selectedUsageRows);

  const openDetailsModal = (promo) => {
    setSelectedPromotion(promo);
    setDetailsModalIsOpen(true);
  };

  const closeDetailsModal = () => {
    setSelectedPromotion(null);
    setDetailsModalIsOpen(false);
  };


  return (
    <>
      <MenuHeader />
      <div className="bloc-search bloc-search-mobile">
        <div className="container">
          <div className="search">
            <div className="select">
              <select name="type" value={filters.type} onChange={handleFilterChange}>
                <option value="">{t('filter.allTypes')}</option>
                <option value="PERCENT_OFF">{t('form.type.options.percentOff')}</option>
                <option value="FLATT_OFF">{t('form.type.options.flatOff')}</option>
                {/* <option value="FREE_CREDITS">{t('form.type.options.freeCredits')}</option> */}
                <option value="FREE">{t('form.type.options.freeService')}</option>
                <option value="DURATION">{t('form.type.options.duration')}</option>
              </select>
            </div>
            <form onSubmit={handleFilterSubmit} className="text-search">
              <input
                type="text"
                name="search"
                value={filterData.search}
                onChange={handleFilterChange}
                placeholder={t('placeholder.search')}
                className="form-text"
              />
              <input type="submit" className="form-submit" value={t('button.search')} />
            </form>
          </div>
          <button onClick={() => openModal()} className="btn-add-client">
            {t('button.add')}
          </button>
        </div>
      </div>

      <div className="tableau-list view-mobile">
        <div className="container">
          {translatedPromotions?.length > 0 ? (
            translatedPromotions.map((promo, index) => (
              <div className="list-items" key={promo.id}>
                <div>{t('table.headers.type')} : {promo.is_gift ? t('table.values.gift') : t('table.values.promotion')}</div>
                <div>{t('table.headers.promotionCode')} : {promo.code}</div>
                <div>{t('table.headers.name')} : {promo.name || ''}</div>
                <div>{t('table.headers.dateCreation')} : {formatPromotionDate(promo.date_creation)}</div>
                <div>
                  {t('table.headers.dateValidity')} : {promo.date_validity.startsWith('9999') ? t('table.values.unlimited') : formatPromotionDate(promo.date_validity)}
                </div>
                <div>{t('table.headers.type')} : {typeLabels[promo.type]}</div>
                <div>
                  {t('table.headers.assignedTo')} :
                  {renderAssignedClients(promo)}
                </div>

                <div>
                  Zone : {promo.zones?.length ? promo.zones?.map((zone) => zone?.name).join(", ") : "No Zone assigned"}
                </div>
                <div>{t('table.headers.usage')} : {renderUsageCell(promo)}</div>
                <div>{t('table.headers.remaining')} : {renderRemainingCell(promo)}</div>
                <div>{t('table.headers.payment')} : {renderPaymentCell(promo)}</div>
                <div>
                  {t('table.headers.status')} : {renderBusinessStatus(promo)}
                </div>
                <div className="menu-edit">
                  <IconButton onClick={(event) => handleClick(event, index)}>
                    <MoreVertIcon />
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && selectedIndex === index}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuList style={{ minWidth: 150 }}>
                      <MenuItem onClick={() => { handleClose(); openModal(promotions[index]); }}>
                        <ListItemIcon>
                          {promo.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>{promo.is_active ? t('actions.deactivate') : t('actions.activate')}</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { handleClose(); openDetailsModal(promo); }}>
                        <ListItemIcon>
                          <VisibilityIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{promo.is_gift ? t('actions.giftDetails') : t('actions.details')}</ListItemText>
                      </MenuItem>
                      {promo.is_active && (
                        <MenuItem style={{ color: 'red' }} onClick={() => togglePromotionStatus(promo)}>
                          <ListItemIcon>
                            <BlockIcon style={{ color: "#FF5733" }} />
                          </ListItemIcon>
                          <ListItemText>Deactivate Promotion</ListItemText>
                        </MenuItem>
                      )}
                      {!promo.is_active && (
                        <MenuItem style={{ color: 'green' }} onClick={() => togglePromotionStatus(promo)}>
                          <ListItemIcon>
                            <CheckCircleIcon style={{ color: "#28A745" }} />
                          </ListItemIcon>
                          <ListItemText>Activate Promotion</ListItemText>
                        </MenuItem>
                      )}
                      {alertMessage && (
                        <MenuItem style={{ color: 'gray' }} disabled>
                          <ListItemText><span dangerouslySetInnerHTML={{ __html: alertMessage }} /></ListItemText>
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </div>

              </div>
            ))
          ) : (
            <div>
              <div colSpan="6">{t('table.values.noPromotions')}</div>
            </div>
          )}
        </div>
      </div>

      <div className="tableau-list view-desktop">
        <div className="container">
          <div className="sticky-table-container">
          <table className="sticky-table">
            <thead>
              <tr>
                <th className="sort-header active" id="type" onClick={handleSelectedSort}>{t('table.headers.promotionType')}</th>
                <th className="sort-header active" id="code" onClick={handleSelectedSort}>{t('table.headers.promotionCode')}</th>
                <th className="sort-header active" id="name" onClick={handleSelectedSort}>{t('table.headers.name')}</th>
                <th className="sort-header active" id="date_creation" onClick={handleSelectedSort}>{t('table.headers.dateCreation')}</th>
                <th className="sort-header active" id="date_validity" onClick={handleSelectedSort}>{t('table.headers.dateValidity')}</th>
                <th className="sort-header active" id="type" onClick={handleSelectedSort}>{t('table.headers.type')}</th>
                <th className="sort-header active" id="promo.clients" onClick={handleSelectedSort}>{t('table.headers.assignedTo')}</th>
                <th className="sort-header active" id="promo.zones" onClick={handleSelectedSort}>{t('table.headers.zones')}</th>
                <th>{t('table.headers.usage')}</th>
                <th>{t('table.headers.remaining')}</th>
                <th>{t('table.headers.payment')}</th>
                <th className="sort-header active" id="is_active" onClick={handleSelectedSort}>{t('table.headers.status')}</th>
                <th >{t('table.headers.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {translatedPromotions?.length > 0 ? (
                translatedPromotions.map((promo, index) => (
                  <tr key={promo.id}>
                    <td>{promo.is_gift ? t('table.values.gift') : t('table.values.promotion')}</td>
                    <td>{promo.code}</td>
                    <td>{promo.name || ''}</td>
                    <td>{formatPromotionDate(promo.date_creation)}</td>
                    <td>
                      {promo.date_validity.startsWith('9999') ? t('table.values.unlimited') : formatPromotionDate(promo.date_validity)}
                    </td>
                    <td>{typeLabels[promo.type]}</td>
                    <td>
                      {renderAssignedClients(promo)}
                    </td>

                    <td>
                      {promo.zones?.length ? promo.zones?.map((zone) => zone?.name).join(", ") : t('table.values.noZone')}
                    </td>
                    <td>{renderUsageCell(promo)}</td>
                    <td>{renderRemainingCell(promo)}</td>
                    <td>{renderPaymentCell(promo)}</td>
                    <td>{renderBusinessStatus(promo)}</td>
                    <td>
                      <IconButton onClick={(event) => handleClick(event, index)}>
                        <MoreVertIcon />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedIndex === index}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <MenuList style={{ minWidth: 150 }}>
                          <MenuItem onClick={() => { handleClose(); openModal(promotions[index]); }}>
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('actions.edit')}</ListItemText>
                          </MenuItem>
                          <MenuItem onClick={() => { handleClose(); openDetailsModal(promo); }}>
                            <ListItemIcon>
                              <VisibilityIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{promo.is_gift ? t('actions.giftDetails') : t('actions.details')}</ListItemText>
                          </MenuItem>
                          {promo.is_active && (
                            <MenuItem style={{ color: 'red' }} onClick={() => togglePromotionStatus(promo)}>
                              <ListItemIcon>
                                <BlockIcon style={{ color: "#FF5733" }} />
                              </ListItemIcon>
                              <ListItemText>{t('actions.deactivate')}</ListItemText>
                            </MenuItem>
                          )}
                          {!promo.is_active && (
                            <MenuItem style={{ color: 'green' }} onClick={() => togglePromotionStatus(promo)}>
                              <ListItemIcon>
                                <CheckCircleIcon style={{ color: "#28A745" }} />
                              </ListItemIcon>
                              <ListItemText>{t('actions.activate')}</ListItemText>
                            </MenuItem>
                          )}
                          {alertMessage && (
                            <MenuItem style={{ color: 'gray' }} disabled>
                              <ListItemText><span dangerouslySetInnerHTML={{ __html: alertMessage }} /></ListItemText>
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13">{t('table.values.noPromotions')}</td>
                </tr>
              )}
            </tbody>
            <tfoot >
              <tr>
                <td colSpan={13}>
                  <div className="paginator">
                    <select value={perPage} onChange={handlePerPageChange} className="per-page">
                      <option value={1}>1</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                    </select>
                    <div className="total">{t('paginator.total')}{total}</div>
                    <div className="pagination">
                      <button type="button" disabled={currentPage === 1} onClick={handleFirstPage}>{'<<'}</button>
                      <button type="button" disabled={currentPage === 1} onClick={handlePrevPage}>{'<'}</button>
                      <span>{currentPage} / {lastPage}</span>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleNextPage}>{'>'}</button>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleLastPage}>{'>>'}</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel={selectedPromotion ? "Edit Promotion" : "Create Promotion"}
      >
        <button className="close-button" onClick={closeModal}>
          <span className="close-icon">&times;</span>
        </button>

        <h2>{selectedPromotion ? t('modal.edit') : t('modal.create')}</h2>
        <div className="form-inscription">
          <form onSubmit={handleFormSubmit}>
            <div className="form-text">
              <label>{t('form.code.label')}</label>
              <input type="text" name="code" value={promotionFormData.code} onChange={handleInputChange} />
            </div>

            <div className="form-text">
              <label>{t('form.name.label')}</label>
              <MultilingualInput
                value={promotionFormData.name}
                onChange={(value) => handleMultilingualChange('name', value)}
                placeholder={t('form.name.placeholder', 'Enter promotion name')}
                inputClass="input-style"
              />
            </div>
            <div className="form-text">
              {/* <label>Date of creation*</label> */}
              <input type="hidden" name="date_creation" value={promotionFormData.date_creation} onChange={handleInputChange} />
            </div>

            <div className="champs-notif">
              <input
                type="checkbox"
                name="unlimited_validity"
                checked={promotionFormData.date_validity.startsWith('9999')}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  handleInputChange({
                    target: { name: "date_validity", value: isChecked ? '9999-09-09T00:00:00.000000Z' : '' },
                  });
                }}
              />
              <label>
                {t('form.unlimitedValidity.label')}
              </label>
            </div>
            <br />

            {!promotionFormData.date_validity.startsWith('9999') && (
              <div className="form-text">
                <label>{t('form.dateValidity.label')} *</label>
                <input
                  type="date"
                  name="date_validity"
                  value={promotionFormData.date_validity ? promotionFormData.date_validity.split('T')[0] : ''}
                  onChange={handleInputChange}
                  required={!promotionFormData.date_validity.startsWith('9999')}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

            )}

            <div className="form-select">
              <label>{t('form.type.label')} *</label>
              <select name="type" value={promotionFormData.type} onChange={handleTypeChange} required>
                <option value="PERCENT_OFF">{t('form.type.options.percentOff')}</option>
                <option value="FLATT_OFF">{t('form.type.options.flatOff')}</option>
                {/* <option value="FREE_CREDITS">{t('form.type.options.freeCredits')}</option> */}
                <option value="FREE">{t('form.type.options.freeService')}</option>
                <option value="DURATION">{t('form.type.options.duration')}</option>
              </select>
            </div>

            <div className="form-text">
              {promotionFormData.type === "PERCENT_OFF" && (
                <>
                  <label>{t('form.percentage.label')} *</label>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="number"
                      name="amount"
                      value={promotionFormData.amount}
                      onChange={handleInputChange}
                      required
                      min="1"
                      max="100"
                      style={{ flex: 1 }}
                    />
                    <span style={{ marginLeft: "10px" }}>%</span>
                  </div>
                </>
              )}

              {(promotionFormData.type === "FLATT_OFF" || promotionFormData.type === "FREE_CREDITS") && (
                <>
                  <label>{t('form.amount.label')} *</label>
                  <input
                    type="number"
                    name="amount"
                    value={promotionFormData.amount}
                    onChange={handleInputChange}
                    required
                    min="0"
                  />
                </>
              )}

              {(promotionFormData.type === "DURATION") && (
                <>
                  <label>Duration *</label>
                  <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="number"
                    name="amount"
                    value={promotionFormData.amount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    />
                    <span style={{ marginLeft: "10px" }}>min</span>
                  </div>
                </>
              )}

              {promotionFormData.type !== "DURATION" && (
                <>
                  <label>{t('form.product.label')}</label>
                  <Select
                    // isMulti
                    isClearable
                    options={productOptions}
                    value={selectedProductOption}
                    onChange={(selectedOption) =>
                      setPromotionFormData((prev) => ({
                        ...prev,
                        product_id: selectedOption ? selectedOption.value : null,
                        amount: prev.type === "FREE" ? selectedOption ? selectedOption.price : null : prev.amount,
                      }))
                    }
                    styles={customSelectStyles}
                    placeholder={t('form.product.placeholder')}
                  />
                </>
              )}
            </div>

            <div className="form-text">
              <label>{t('form.zones.label')}</label>
              <Select
                isMulti
                options={zoneOptions}
                value={selectedZones}
                onChange={handleZonesChange}
                styles={customSelectStyles}
                placeholder={t('form.zones.placeholder')}
              />
            </div>

            <div className="form-select">
              <label>{t('form.assignedTo.label')} *</label>
              <select name="assigned_to" value={promotionFormData.assigned_to} onChange={handleInputChange} required>
                <option value="One client">{t('form.assignedTo.options.oneClient')}</option>
                <option value="Group of clients">{t('form.assignedTo.options.groupClients')}</option>
                <option value="All">{t('form.assignedTo.options.allClients')}</option>
              </select>
            </div>


            {(promotionFormData.assigned_to === "One client" || promotionFormData.assigned_to === "Group of clients") && (
              <div className="form-text">
                <label>{t('form.clients.label')}</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={clientOptions}
                  defaultInputValue={selectedClients.label || ''}
                  loadOptions={loadClientsOptions}
                  isMulti={promotionFormData.assigned_to === "Group of clients"}
                  options={clientOptions}
                  value={selectedClients}
                  onChange={handleClientsChange}
                  styles={customSelectStyles}
                  placeholder={t('form.clients.placeholder')}
                />
              </div>
            )}
            {formError && <span className="error-message" role="alert">{formError}</span>}

            <div className="champs-notif">
              <input
                type="checkbox"
                name="unlimited_usage"
                checked={promotionFormData.total_usage === -1 && promotionFormData.usage_per_customer === -1}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  handleInputChange({
                    target: { name: "total_usage", value: isChecked ? -1 : "" },
                  });
                  handleInputChange({
                    target: { name: "usage_per_customer", value: isChecked ? -1 : "" },
                  });
                }}
              />
              <label>{t('form.usage.unlimited')}</label>
            </div>
            <br />

            {!(promotionFormData.total_usage === -1 && promotionFormData.usage_per_customer === -1) && (
              <>
                <div className="form-text">
                  <label>{t('form.usage.total')} *</label>
                  <input
                    type="number"
                    name="total_usage"
                    value={promotionFormData.total_usage}
                    onChange={handleInputChange}
                    required={promotionFormData.total_usage !== -1}
                  />
                </div>

                <div className="form-text">
                  <label>{t('form.usage.perCustomer')} *</label>
                  <input
                    type="number"
                    name="usage_per_customer"
                    value={promotionFormData.usage_per_customer}
                    onChange={handleInputChange}
                    required={promotionFormData.usage_per_customer !== -1}
                  />
                </div>
              </>
            )}


            <div className="champs-notif">
              <input type="checkbox" name="notifyClients" checked={promotionFormData.notifyClients} onChange={handleInputChange} />
              <label>{t('form.notification.send')}</label>
            </div>

            <div className="champs-notif">
              <input type="checkbox" name="notifyEmails" checked={promotionFormData.notifyEmails} onChange={handleInputChange} />
              <label>{t('form.notification.email')}</label>
            </div>

            <button type="submit" className="btn-submit">{selectedPromotion ? t('form.submit.update') : t('form.submit.create')}</button>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={detailsModalIsOpen}
        onRequestClose={closeDetailsModal}
        style={customStyles}
      >
        <button className="close-button" onClick={closeDetailsModal}>
          <span className="close-icon">✖</span>
        </button>
        {selectedPromotion && (
          <div className="form-inscription mrgBottom">
            <h2>{t('details.title', { name: getDisplayName(selectedPromotion.name) })}</h2><br />
            <h2>{t('details.promotion.title')}</h2><br />
            <form>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.promotion.code')}</label>
                <input type="text" name="promotion_code" value={selectedPromotion.code || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.promotion.type')}</label>
                <input type="text" name="promotion_type" value={typeLabels[selectedPromotion.type] || selectedPromotion.type || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.promotion.usage')}</label>
                <input
                  type="text"
                  name="promotion_usage"
                  value={`${selectedUsageSummary?.used || 0} / ${formatLimit(selectedUsageSummary?.totalLimit)}`}
                  readOnly
                />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.promotion.remaining')}</label>
                <input
                  type="text"
                  name="promotion_remaining"
                  value={selectedUsageSummary?.isUnlimited ? '∞' : (selectedUsageSummary?.remaining ?? '-')}
                  readOnly
                />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.promotion.usagePerCustomer')}</label>
                <input type="text" name="promotion_usage_per_customer" value={formatLimit(selectedPromotion.usage_per_customer)} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.promotion.status')}</label>
                <div style={{ padding: '18px 24px' }}>{renderBusinessStatus(selectedPromotion)}</div>
              </div>
            </form>
            {selectedPromotion.is_gift && (
              <>
                <h2>{t('details.gift.title')}</h2><br />
                <form>
                  <div className="input-hotel-container">
                    <label className="input-hotel-label">{t('details.fromName')}</label>
                    <input type="text" name="name" value={selectedPromotion.from_user_name || '-'} readOnly />
                  </div>
                  <div className="input-hotel-container">
                    <label className="input-hotel-label">{t('details.buyerEmail')}</label>
                    <input type="text" name="buyer_email" value={selectedPromotion.buyer_email || '-'} readOnly />
                  </div>
                  <div className="input-hotel-container">
                    <label className="input-hotel-label">{t('details.toName')}</label>
                    <input type="text" name="email" value={selectedPromotion.to_user_name || '-'} readOnly />
                  </div>
                  <div className="input-hotel-container">
                    <label className="input-hotel-label">{t('details.toEmail')}</label>
                    <input type="text" name="phone" value={selectedPromotion.to_user_email || '-'} readOnly />
                  </div>
                  <div className="input-hotel-container">
                    <label className="input-hotel-label">{t('details.message')}</label>
                    <input type="text" name="registration_date" value={getDisplayName(selectedPromotion.long_description) || '-'} readOnly />
                  </div>
                  <div className="input-hotel-container">
                    <label className="input-hotel-label">{t('details.physicalCard')}</label>
                    <input type="text" name="physical_card" value={selectedPromotion.address_id ? t('details.yes') : t('details.no')} readOnly />
                  </div>
                  {selectedPromotion.address?.zip_code && (
                    <>
                      <div className="input-hotel-container">
                        <label className="input-hotel-label">{t('details.address')}</label>
                        <input type="text" name="status" value={`${selectedPromotion.address?.street} ${selectedPromotion.address?.street_number}, ${selectedPromotion.address?.zip_code} ${selectedPromotion.address?.state}`} readOnly />
                      </div>
                    </>
                  )}
                </form>
              </>
            )}
            <h2>{t('details.transaction.title')}</h2><br />
            <form>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.id')}</label>
                <input type="text" name="transaction_id" value={selectedTransaction?.transaction_id || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.status')}</label>
                <input type="text" name="status" value={selectedTransaction?.status || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.amount')}</label>
                <input
                  type="text"
                  name="amount"
                  value={selectedTransaction ? `${selectedTransaction.amount} ${selectedTransaction.currency}` : '-'}
                  readOnly
                />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.invoiceId')}</label>
                <input type="text" name="invoice_id" value={selectedTransaction?.invoice_id || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.paymentMethod')}</label>
                <input type="text" name="payment_method" value={selectedTransaction?.payment_method || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.succeededAt')}</label>
                <input type="text" name="succeeded_at" value={formatPromotionDateTime(selectedTransaction?.succeeded_at) || '-'} readOnly />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('details.transaction.failedAt')}</label>
                <input type="text" name="failed_at" value={formatPromotionDateTime(selectedTransaction?.failed_at) || '-'} readOnly />
              </div>
            </form>
            <h2>{t('details.usageHistory.title')}</h2><br />
            {selectedUsageGroups.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {selectedUsageGroups.map((group) => {
                  const booking = group.booking;
                  const discountTotal = group.usageRows.reduce((sum, usage) => sum + Number(usage?.value || 0), 0);
                  const productTotal = getBookingProductTotal(booking);
                  const bookingTotal = booking ? Number(booking.total_fare ?? booking.subtotal ?? Math.max(productTotal - discountTotal, 0)) : null;
                  const clientLabel = getUsageClientLabel(group.usageRows[0]);
                  const flypros = Array.isArray(booking?.therapists)
                    ? booking.therapists.map((therapist) => therapist.extra?.full_name || therapist.firstname || therapist.email).filter(Boolean).join(', ')
                    : '';

                  if (!booking) {
                    const usage = group.usageRows[0];

                    return (
                      <div
                        key={group.key}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1.4fr 1.2fr 1fr 0.9fr',
                          gap: 14,
                          alignItems: 'center',
                          background: '#fff',
                          borderRadius: 12,
                          padding: '14px 18px',
                        }}
                      >
                        <span>{formatPromotionDateTime(usage.created_at) || '-'}</span>
                        <span>{clientLabel}</span>
                        <span>{formatMoney(usage.value)}</span>
                        <span style={statusPillStyle(usage.is_used ? 'success' : 'neutral')}>
                          {usage.is_used ? t('details.usageHistory.used') : t('details.usageHistory.reserved')}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div key={group.key} style={{ background: '#fff', borderRadius: 12, padding: 18 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 14 }}>
                        <div>
                          <strong>{t('details.usageHistory.orderNumber', { id: booking.id })}</strong>
                          <div style={{ color: '#777', fontSize: 13 }}>{formatPromotionDateTime(booking.start_before || booking.date) || '-'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ minWidth: 110 }}>
                            <StatusBadge status={booking.status} namespace="statusManager" />
                          </div>
                          <a href={`/tasks?id=${booking.id}`} style={statusPillStyle('neutral')}>
                            {t('details.usageHistory.openOrder')}
                          </a>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 14 }}>
                        <div><small>{t('details.usageHistory.client')}</small><br />{clientLabel}</div>
                        <div><small>{t('details.usageHistory.flypros')}</small><br />{flypros || '-'}</div>
                        <div><small>{t('details.usageHistory.duo')}</small><br />{booking.isDuo ? t('details.yes') : t('details.no')}</div>
                        <div><small>{t('details.usageHistory.address')}</small><br />{getBookingAddressLabel(booking)}</div>
                        <div><small>{t('details.usageHistory.payment')}</small><br />{booking.offline_payment ? t('details.usageHistory.packagePayment') : getBookingPaymentLabel(booking)}</div>
                        <div><small>{t('details.usageHistory.finalTotal')}</small><br />{formatMoney(bookingTotal)}</div>
                      </div>

                      <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginBottom: 12 }}>
                        <strong>{t('details.usageHistory.services')}</strong>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                          {(booking.products || []).map((product, index) => (
                            <div key={`${product.id}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                              <span>{getProductLabel(product)}</span>
                              <span>{formatMoney(product.price)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 12 }}>
                        <div><small>{t('details.usageHistory.originalTotal')}</small><br />{formatMoney(productTotal)}</div>
                        <div><small>{t('details.usageHistory.discountTotal')}</small><br />{formatMoney(discountTotal)}</div>
                        <div><small>{t('details.usageHistory.unitsConsumed')}</small><br />{group.usageRows.length}</div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {group.usageRows.map((usage, index) => (
                          <div
                            key={usage.id || index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr 1fr 0.8fr',
                              gap: 8,
                              alignItems: 'center',
                              background: '#F8F8F8',
                              borderRadius: 10,
                              padding: '10px 12px',
                            }}
                          >
                            <span>{t('details.usageHistory.packageUnit')}</span>
                            <span>{t('details.usageHistory.discount')}: {formatMoney(usage.value)}</span>
                            <span>{formatPromotionDateTime(usage.created_at) || '-'}</span>
                            <span style={statusPillStyle(usage.is_used ? 'success' : 'neutral')}>
                              {usage.is_used ? t('details.usageHistory.used') : t('details.usageHistory.reserved')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="input-hotel-container">
                <input type="text" name="no_usage" value={t('details.usageHistory.noUsage')} readOnly />
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default Promotion;
